import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { WebPubSubClient } from '@azure/web-pubsub-client';
import { apiGet } from '../utils/apiClient';
import { hasToken } from '../utils/tokenStorage';
import {
  MEASUREMENT_APP_EVENT,
  MEASUREMENT_CREATION_EVENT_TYPE,
} from '../constants/measurementEvents';
import { MEASUREMENT_API_BASE } from '../services/measurementService';

export interface MeasurementAppEventPayload {
  type?: string;
  measurementId?: string;
  status?: string;
  message?: string | null;
}

export interface TenantRealtimeToast {
  id: string;
  title: string;
  body?: string;
  createdAt: number;
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'unavailable' | 'error';

interface WebPubSubContextValue {
  connectionState: ConnectionState;
  /** FIFO queue (oldest first). UI should show at most `toasts[0]` until dismissed. */
  toasts: TenantRealtimeToast[];
  dismissToast: (id: string) => void;
}

const WebPubSubContext = createContext<WebPubSubContextValue | null>(null);

function parsePayload(data: unknown): MeasurementAppEventPayload | null {
  try {
    let raw: unknown;
    if (data instanceof ArrayBuffer) {
      raw = JSON.parse(new TextDecoder().decode(data));
    } else if (typeof data === 'string') {
      raw = JSON.parse(data);
    } else if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
      raw = data;
    } else {
      return null;
    }
    if (typeof raw !== 'object' || raw === null) {
      return null;
    }
    return raw as MeasurementAppEventPayload;
  } catch {
    return null;
  }
}

function toastFromPayload(payload: MeasurementAppEventPayload): TenantRealtimeToast | null {
  if (payload.type !== MEASUREMENT_CREATION_EVENT_TYPE) {
    return null;
  }
  const st = (payload.status || '').toLowerCase();
  const id = payload.measurementId;
  if (!id) {
    return null;
  }
  if (st === 'reported' || st === 'upcoming') {
    return {
      id: `${Date.now()}-${id}-${st}`,
      title: 'Measurement ready',
      body: st === 'reported' ? 'You can open the report now.' : 'You can open the measurement now.',
      createdAt: Date.now(),
    };
  }
  if (st === 'failed') {
    return {
      id: `${Date.now()}-${id}-failed`,
      title: 'Measurement failed',
      body: payload.message || 'Processing did not complete.',
      createdAt: Date.now(),
    };
  }
  return null;
}

export const WebPubSubProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [toasts, setToasts] = useState<TenantRealtimeToast[]>([]);
  const clientRef = useRef<WebPubSubClient | null>(null);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => {
      if (prev.length === 0 || prev[0].id !== id) {
        return prev;
      }
      return prev.slice(1);
    });
  }, []);

  const skipRealtime =
    location.pathname === '/login' ||
    location.pathname === '/password-reset' ||
    location.pathname.startsWith('/login/') ||
    location.pathname.startsWith('/password-reset/');

  useEffect(() => {
    if (skipRealtime || !hasToken()) {
      clientRef.current?.stop();
      clientRef.current = null;
      setConnectionState('disconnected');
      return undefined;
    }

    let cancelled = false;

    const fetchClientAccessUrl = async (): Promise<string> => {
      const res = await apiGet(`${MEASUREMENT_API_BASE}/events`);
      if (res.status === 503) {
        throw Object.assign(new Error('Realtime messaging is not configured.'), {
          code: 'UNAVAILABLE' as const,
        });
      }
      if (!res.ok) {
        throw new Error('Failed to get realtime connection URL.');
      }
      const body = (await res.json()) as { url?: string };
      const url = body.url;
      if (!url) {
        throw new Error('Realtime URL missing from response.');
      }
      return url;
    };

    const run = async () => {
      setConnectionState('connecting');

      const client = new WebPubSubClient(
        { getClientAccessUrl: fetchClientAccessUrl },
        {
          autoReconnect: true,
          autoRejoinGroups: true,
        }
      );

      const handleData = (data: unknown) => {
        const payload = parsePayload(data);
        if (!payload) {
          return;
        }
        window.dispatchEvent(
          new CustomEvent<MeasurementAppEventPayload>(MEASUREMENT_APP_EVENT, { detail: payload })
        );
        const toast = toastFromPayload(payload);
        if (toast) {
          setToasts((prev) => {
            if (prev.length >= 12) {
              return prev;
            }
            return [...prev, toast];
          });
        }
      };

      const onServerMessage = (e: { message: { data: unknown } }) => {
        handleData(e.message.data);
      };
      const onGroupMessage = (e: { message: { data: unknown } }) => {
        handleData(e.message.data);
      };

      client.on('server-message', onServerMessage);
      client.on('group-message', onGroupMessage);

      if (cancelled) {
        client.stop();
        return;
      }

      try {
        await client.start();
      } catch (e: unknown) {
        if (!cancelled) {
          const code =
            e && typeof e === 'object' && 'code' in e
              ? (e as { code?: string }).code
              : undefined;
          setConnectionState(code === 'UNAVAILABLE' ? 'unavailable' : 'error');
        }
        return;
      }
      if (cancelled) {
        client.stop();
        return;
      }
      clientRef.current = client;
      setConnectionState('connected');
    };

    void run();

    return () => {
      cancelled = true;
      clientRef.current?.stop();
      clientRef.current = null;
      setConnectionState('disconnected');
    };
  }, [skipRealtime]);

  const value: WebPubSubContextValue = {
    connectionState,
    toasts,
    dismissToast,
  };

  return <WebPubSubContext.Provider value={value}>{children}</WebPubSubContext.Provider>;
};

export function useWebPubSub(): WebPubSubContextValue {
  const ctx = useContext(WebPubSubContext);
  if (!ctx) {
    throw new Error('useWebPubSub must be used within WebPubSubProvider');
  }
  return ctx;
}
